import React, { useState, useEffect, useCallback } from 'react';
import { Slide, AspectRatio, ThemeFont, SlideBorderStyle, Theme } from '../types';
import { SlideRenderer } from './SlideRenderer';
import { X, ChevronLeft, ChevronRight, SkipBack, SkipForward, Maximize, Minimize } from 'lucide-react';

interface PresentationModalProps {
  slides: Slide[];
  aspectRatio: AspectRatio;
  theme: Theme;
  themeFont?: ThemeFont;
  slideBorderStyle?: SlideBorderStyle;
  language: 'ko' | 'en';
  isDarkMode?: boolean;
  onClose: () => void;
}

export const PresentationModal: React.FC<PresentationModalProps> = ({
  slides,
  aspectRatio,
  theme,
  themeFont,
  slideBorderStyle,
  language,
  isDarkMode = false,
  onClose
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [slideScale, setSlideScale] = useState(1);
  const totalSlides = slides.length;

  const translations = {
    ko: {
      presentationMode: '프레젠테이션 모드',
      slide: '슬라이드',
      of: '/',
      close: '닫기',
      fullscreen: '전체화면',
      exitFullscreen: '전체화면 종료',
      first: '첫 슬라이드',
      previous: '이전',
      next: '다음',
      last: '마지막 슬라이드',
      controls: '← → 화살표 키, 스페이스바, 마우스 휠 또는 클릭으로 이동'
    },
    en: {
      presentationMode: 'Presentation Mode',
      slide: 'Slide',
      of: 'of',
      close: 'Close',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit Fullscreen',
      first: 'First Slide',
      previous: 'Previous',
      next: 'Next',
      last: 'Last Slide',
      controls: 'Use ← → arrow keys, spacebar, mouse wheel or click to navigate'
    }
  };

  const t = translations[language];

  // 슬라이드 스케일 계산
  const calculateSlideScale = useCallback(() => {
    const containerWidth = window.innerWidth - (isFullscreen ? 40 : 80);
    const containerHeight = window.innerHeight - (isFullscreen ? 80 : 200); // 상단바와 하단바 고려
    
    // 높이를 기준으로 스케일 계산 (높이에 맞춰 꽉 차게)
    const scaleByHeight = containerHeight / aspectRatio.height;
    const scaleByWidth = containerWidth / aspectRatio.width;
    
    // 더 작은 스케일을 사용하여 화면에 맞춤 (최대 1배)
    const scale = Math.min(scaleByHeight, scaleByWidth, 1);
    
    setSlideScale(scale);
  }, [aspectRatio, isFullscreen]);

  // 화면 크기 변경 감지
  useEffect(() => {
    calculateSlideScale();
    
    const handleResize = () => {
      calculateSlideScale();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateSlideScale]);

  // 전체화면 상태 변경 시 스케일 재계산
  useEffect(() => {
    calculateSlideScale();
  }, [isFullscreen, calculateSlideScale]);

  // 슬라이드 업데이트
  const updateSlide = useCallback(() => {
    const progress = ((currentSlide + 1) / totalSlides) * 100;
    const progressBar = document.querySelector('.progress-fill') as HTMLElement;
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }
  }, [currentSlide, totalSlides]);

  // 다음 슬라이드
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);

  // 이전 슬라이드
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  // 첫 슬라이드로
  const firstSlide = useCallback(() => {
    setCurrentSlide(0);
  }, []);

  // 마지막 슬라이드로
  const lastSlide = useCallback(() => {
    setCurrentSlide(totalSlides - 1);
  }, [totalSlides]);

  // 전체화면 토글
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Fullscreen operation failed:', error);
    }
  }, []);

  // 키보드 이벤트 처리
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'Home':
          e.preventDefault();
          firstSlide();
          break;
        case 'End':
          e.preventDefault();
          lastSlide();
          break;
        case 'Escape':
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            onClose();
          }
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, firstSlide, lastSlide, onClose, toggleFullscreen]);

  // 마우스 휠 이벤트 처리
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY > 0) {
        nextSlide();
      } else if (e.deltaY < 0) {
        prevSlide();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, [nextSlide, prevSlide]);

  // 전체화면 상태 변경 감지
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // 마우스 움직임 감지 (컨트롤 표시/숨김)
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (isFullscreen) {
          setShowControls(false);
        }
      }, 3000);
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, [isFullscreen]);

  // 진행률 업데이트
  useEffect(() => {
    updateSlide();
  }, [updateSlide]);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* 상단 컨트롤바 */}
      <div 
        className={`flex items-center justify-between p-4 bg-black bg-opacity-75 text-white transition-all duration-300 ${
          showControls || !isFullscreen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">{t.presentationMode}</h2>
          <div className="text-sm opacity-75">
            {t.slide} {currentSlide + 1} {t.of} {totalSlides}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={firstSlide}
            disabled={currentSlide === 0}
            className="p-2 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t.first}
          >
            <SkipBack className="w-4 h-4" />
          </button>
          
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="p-2 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t.previous}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <button
            onClick={nextSlide}
            disabled={currentSlide === totalSlides - 1}
            className="p-2 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t.next}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          
          <button
            onClick={lastSlide}
            disabled={currentSlide === totalSlides - 1}
            className="p-2 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={t.last}
          >
            <SkipForward className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-white bg-opacity-30 mx-2"></div>
          
          <button
            onClick={toggleFullscreen}
            className="p-2 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
            title={isFullscreen ? t.exitFullscreen : t.fullscreen}
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
          
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
            title={t.close}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 메인 슬라이드 영역 */}
      <div 
        className="flex-1 flex items-center justify-center cursor-pointer relative"
        onClick={nextSlide}
        style={{ 
          minHeight: '0',  // flex 아이템이 축소될 수 있도록
          height: '100%'   // 전체 높이 사용
        }}
      >
        <div 
          className="flex items-center justify-center w-full h-full"
          style={{
            padding: isFullscreen ? '20px' : '40px'  // 전체화면일 때 패딩 조정
          }}
        >
          <div 
            style={{
              width: `${aspectRatio.width}px`,
              height: `${aspectRatio.height}px`,
              transform: `scale(${slideScale})`,
              transformOrigin: 'center center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SlideRenderer
              slide={slides[currentSlide]}
              aspectRatio={aspectRatio}
              theme={theme}
              themeFont={themeFont}
              borderStyle={slideBorderStyle}
              isEditable={false}
              isDarkMode={isDarkMode}
              language={language}
              scale={1}
            />
          </div>
        </div>
      </div>

      {/* 하단 컨트롤 및 진행률 */}
      <div 
        className={`transition-all duration-300 ${
          showControls || !isFullscreen ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* 진행률 바 */}
        <div className="w-full h-1 bg-white bg-opacity-20">
          <div 
            className="progress-fill h-full bg-green-500 transition-all duration-300"
            style={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
          ></div>
        </div>
        
        {/* 컨트롤 가이드 */}
        <div className="text-center text-white text-sm opacity-75 py-2">
          {t.controls}
        </div>
      </div>
    </div>
  );
};